import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  FaCreditCard, FaHandshake, FaExchangeAlt, FaChartLine,
  FaCloud, FaCode, FaArrowRight, FaCheckCircle, FaCogs,
  FaDatabase, FaMobileAlt, FaLayerGroup, FaRocket
} from 'react-icons/fa';
import './Services.css';

const services = [
  {
    id: 'payment-gateway',
    icon: <FaCreditCard />,
    title: 'Payment Gateway Solutions',
    tagline: 'Seamless, Secure, Scalable',
    desc: 'Build and deploy robust payment gateway infrastructure that handles millions of transactions with high availability, low latency, and full PCI DSS compliance.',
    features: [
      'Multi-currency & multi-bank support',
      'UPI, Net Banking, Cards, Wallets',
      'Real-time transaction monitoring',
      'Webhook & API-first architecture',
      'PCI DSS Level 1 compliance',
      'Fraud detection & chargeback management',
      '99.99% uptime SLA',
      'Merchant dashboard & analytics',
    ],
    tech: ['Node.js', 'Java Spring', 'PostgreSQL', 'Redis', 'Kafka', 'AWS'],
  },
  {
    id: 'escrow',
    icon: <FaHandshake />,
    title: 'Escrow Payment Systems',
    tagline: 'Trust. Security. Transparency.',
    desc: 'Digital escrow platforms that protect all parties in a transaction by holding funds securely until all conditions and milestones are verifiably met.',
    features: [
      'Conditional fund release logic',
      'Multi-party escrow workflows',
      'Milestone-based disbursements',
      'Dispute resolution engine',
      'KYC/AML verification built-in',
      'Legal document integration',
      'Real-time status notifications',
      'Audit trail & compliance reports',
    ],
    tech: ['Python', 'Django', 'PostgreSQL', 'Celery', 'Docker', 'GCP'],
  },
  {
    id: 'reconciliation',
    icon: <FaExchangeAlt />,
    title: 'Reconciliation Engine',
    tagline: 'Automate. Match. Settle.',
    desc: 'High-performance automated reconciliation systems that match millions of transactions daily across payment gateways, banks, and internal ledgers.',
    features: [
      'Multi-source data ingestion',
      'Intelligent transaction matching',
      'Exception management workflows',
      'Real-time & batch reconciliation',
      'Settlement processing automation',
      'ERP & accounting system integration',
      'Customizable matching rules engine',
      'Analytics & exception reporting',
    ],
    tech: ['Java', 'Spring Batch', 'Oracle DB', 'Apache Spark', 'Airflow', 'Azure'],
  },
  {
    id: 'finance-apps',
    icon: <FaChartLine />,
    title: 'Finance Applications',
    tagline: 'Custom. Scalable. Compliant.',
    desc: 'Custom-built financial applications including lending platforms, treasury management, investment tools, and comprehensive financial dashboards.',
    features: [
      'Lending & loan management systems',
      'Treasury & cash flow management',
      'Investment portfolio tracking',
      'Financial reporting & analytics',
      'Regulatory compliance dashboards',
      'Risk assessment engines',
      'Multi-entity accounting systems',
      'Real-time financial data feeds',
    ],
    tech: ['React', 'Node.js', 'MongoDB', 'Elasticsearch', 'RabbitMQ', 'AWS'],
  },
  {
    id: 'cloud',
    icon: <FaCloud />,
    title: 'Cloud Services',
    tagline: 'Migrate. Scale. Optimize.',
    desc: 'End-to-end cloud solutions from assessment and migration to managed services, with fintech-specific security controls and compliance frameworks.',
    features: [
      'Cloud readiness assessment',
      'AWS, Azure & GCP expertise',
      'PCI DSS cloud architecture',
      'Zero-downtime migration',
      'Kubernetes & container orchestration',
      'CI/CD pipeline setup',
      'Cost optimization & FinOps',
      'Disaster recovery planning',
    ],
    tech: ['AWS', 'Azure', 'GCP', 'Kubernetes', 'Terraform', 'Docker'],
  },
  {
    id: 'api-integration',
    icon: <FaLayerGroup />,
    title: 'API Integration Services',
    tagline: 'Connect. Integrate. Automate.',
    desc: 'Seamless API integration services connecting your fintech stack with banks, payment networks, third-party services, and regulatory systems.',
    features: [
      'Bank API integrations (CBS, SFMS)',
      'Payment network connectivity',
      'Third-party KYC/AML APIs',
      'NPCI & RBI system integrations',
      'RESTful & SOAP API development',
      'API gateway & rate limiting',
      'SDK development & documentation',
      'Webhook management systems',
    ],
    tech: ['Node.js', 'OpenAPI', 'Kong', 'Postman', 'JWT', 'GraphQL'],
  },
];

const process = [
  { icon: <FaRocket />, step: '01', title: 'Discovery', desc: 'Deep dive into your requirements, business model, and compliance needs.' },
  { icon: <FaCogs />, step: '02', title: 'Architecture', desc: 'Design scalable, secure, and compliant system architecture.' },
  { icon: <FaCode />, step: '03', title: 'Development', desc: 'Agile development with regular sprints, demos, and quality gates.' },
  { icon: <FaDatabase />, step: '04', title: 'Testing & QA', desc: 'Rigorous security testing, performance testing, and UAT cycles.' },
  { icon: <FaCloud />, step: '05', title: 'Deployment', desc: 'Zero-downtime deployment with monitoring and rollback capabilities.' },
  { icon: <FaMobileAlt />, step: '06', title: 'Support', desc: 'Ongoing maintenance, SLA-backed support, and continuous improvements.' },
];

function Services() {
  const location = useLocation();
  const navigate = useNavigate();

  const serviceId = new URLSearchParams(location.search).get('service');
  const activeService = Math.max(0, services.findIndex(s => s.id === serviceId));

  useEffect(() => {
    if (serviceId) {
      const el = document.getElementById('service-explorer');
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    }
  }, [serviceId]);

  const handleServiceSelect = (idx) => {
    navigate(`/services?service=${services[idx].id}`, { replace: true });
  };

  return (
    <div className="services-page">
      <Helmet>
        <title>Fintech Software Development Services | Techmascot Technologies</title>
        <meta name="description" content="Expert fintech services including Payment Gateway, Escrow Payments, Reconciliation Engine, Finance Applications, Cloud Services and API Integration." />
        <link rel="canonical" href="https://www.techmascot.com/services" />
        <meta property="og:title" content="Fintech Software Development Services | Techmascot Technologies" />
        <meta property="og:description" content="Expert fintech services including Payment Gateway, Escrow Payments, Reconciliation Engine, Finance Applications, Cloud Services and API Integration." />
        <meta property="og:url" content="https://www.techmascot.com/services" />
        <meta property="og:type" content="website" />
      </Helmet>
      {/* Banner */}
      <section className="page-banner">
        <div className="banner-bg">
          <div className="banner-grid"></div>
        </div>
        <div className="container">
          <div className="banner-content">
            <span className="tag">What We Build</span>
            <h1>Our <span>Services</span></h1>
            <p>
              End-to-end fintech product engineering — from payment infrastructure
              to cloud platforms, we build the systems that power financial innovation.
            </p>
          </div>
        </div>
        <div className="banner-wave">
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none">
            <path d="M0,30 C480,60 960,0 1440,30 L1440,60 L0,60 Z" fill="#F8FAFC" />
          </svg>
        </div>
      </section>

      {/* Service Explorer */}
      <section className="service-explorer" id="service-explorer">
        <div className="container">
          <div className="section-header">
            <span className="tag">Core Offerings</span>
            <h2 className="section-title">Fintech <span>Engineering</span> Services</h2>
            <p className="section-subtitle">
              Specialized fintech development services built on deep domain expertise,
              modern technology stacks, and security-first principles.
            </p>
          </div>

          <div className="explorer-layout">
            {/* Service Nav */}
            <div className="service-nav">
              {services.map((s, i) => (
                <button
                  key={s.id}
                  className={`service-nav-btn${activeService === i ? ' active' : ''}`}
                  onClick={() => handleServiceSelect(i)}
                >
                  <span className="nav-icon">{s.icon}</span>
                  <span>{s.title}</span>
                </button>
              ))}
            </div>

            {/* Service Detail */}
            <div className="service-detail">
              <div className="svc-detail-header">
                <div className="svc-detail-icon">{services[activeService].icon}</div>
                <div>
                  <p className="svc-tagline">{services[activeService].tagline}</p>
                  <h3>{services[activeService].title}</h3>
                </div>
              </div>
              <p className="svc-detail-desc">{services[activeService].desc}</p>

              <h4 className="svc-features-title">Key Features & Capabilities</h4>
              <div className="svc-features-grid">
                {services[activeService].features.map((f, i) => (
                  <div key={i} className="svc-feature-item">
                    <FaCheckCircle />
                    <span>{f}</span>
                  </div>
                ))}
              </div>

              <div className="svc-tech-stack">
                <span className="tech-label">Technology Stack:</span>
                {services[activeService].tech.map((t, i) => (
                  <span key={i} className="tech-badge">{t}</span>
                ))}
              </div>

              <Link to="/contact" className="btn-primary" style={{ marginTop: '28px', display: 'inline-flex' }}>
                Discuss This Service <FaArrowRight />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="process-section">
        <div className="container">
          <div className="section-header">
            <span className="tag">How We Work</span>
            <h2 className="section-title">Our <span>Delivery Process</span></h2>
            <p className="section-subtitle">
              A structured, agile approach that ensures on-time delivery,
              quality outcomes, and transparent communication throughout.
            </p>
          </div>
          <div className="svc-process-grid">
            {process.map((p, i) => (
              <div key={i} className="svc-process-card">
                <div className="svc-process-num">{p.step}</div>
                <div className="svc-process-icon">{p.icon}</div>
                <h4>{p.title}</h4>
                <p>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="ind-cta">
        <div className="container">
          <div className="ind-cta-inner">
            <h2>Ready to Build Something Great?</h2>
            <p>
              Share your project requirements and let's discuss how Techmascot Technologies
              can build the perfect fintech solution for your business.
            </p>
            <Link to="/contact" className="btn-primary" style={{ background: '#FFFFFF', color: '#003083', borderColor: '#FFFFFF' }}>
              Start Your Project <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Services;
